// Resolves a field's `OptionSource` into the `{ value, label }` array the
// select/radio renderers expect.
//
// Handles both shapes in one hook so SchemaForm never branches on option kind:
//   - inline array  → returned as-is, no request
//   - reference     → fetched (and re-fetched when `dependsOn` changes, which
//                     is how vehicle-model follows vehicle-make)

import { useEffect, useMemo, useRef, useState } from "react";
import { getReference } from "./referenceProvider";

/** @typedef {import("../form/types").OptionSource} OptionSource */

/**
 * @param {OptionSource | undefined} source
 * @param {Record<string, *>} values  current form values, for `dependsOn`
 * @param {number} [retryToken]  bump to force a refetch after a failure
 * @returns {{ options: Array<Object>, loading: boolean, error: Error|null }}
 */
export const useReferenceOptions = (source, values, retryToken = 0) => {
  const isInline = Array.isArray(source);

  // `dependsOn` may name one field or several. A breed is only identifiable
  // from the species *and* the group, so the list stays closed until both are
  // answered rather than showing every breed of every animal.
  const dependsOn = useMemo(() => {
    if (isInline || !source?.dependsOn) return [];
    return Array.isArray(source.dependsOn) ? source.dependsOn : [source.dependsOn];
  }, [isInline, source]);

  // Serialised so the effect re-runs when a dependency's *value* changes, not
  // when the values object is merely re-created.
  const dependencyValues = dependsOn.map((field) => values?.[field] ?? "").join("\u0000");
  const ready = dependsOn.length === 0 || dependsOn.every((field) => values?.[field]);

  const params = useMemo(() => {
    if (isInline || !source) return undefined;
    const resolved = { ...(source.params || {}) };
    for (const field of dependsOn) resolved[field] = values?.[field];
    return resolved;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInline, source, dependsOn, dependencyValues]);

  const [state, setState] = useState({ options: [], loading: false, error: null });
  const requestId = useRef(0);

  useEffect(() => {
    if (isInline || !source?.domain) return;
    // A dependent list stays empty until every parent is chosen, rather than
    // fetching an unfiltered list the user would have to scroll past.
    if (!ready) {
      setState({ options: [], loading: false, error: null });
      return;
    }

    const id = ++requestId.current;
    setState((s) => ({ ...s, loading: true, error: null }));
    getReference(source.domain, params)
      .then((items) => {
        if (id !== requestId.current) return; // a newer request superseded this one
        setState({
          options: items.map((i) => ({
            value: i.code,
            label: i.label,
            hint: i.hint,
            aliases: i.aliases,
          })),
          loading: false,
          error: null,
        });
      })
      .catch((error) => {
        if (id !== requestId.current) return;
        setState({ options: [], loading: false, error });
      });
  }, [isInline, source, params, ready, retryToken]);

  // Presentation for a reference-sourced option — artwork, a badge, the selling
  // points a card reveals on hover — keyed by canonical code.
  //
  // It lives in the journey config rather than the backend catalogue on
  // purpose: the catalogue's job is to be the single authority on *codes*
  // across every carrier, and a photograph is not a code. This keeps the split
  // clean while still letting a carrier-sourced list render as a rich card.
  const decorated = useMemo(() => {
    const decorate = isInline ? null : source?.decorate;
    if (!decorate) return state.options;
    return state.options.map((o) => ({ ...o, ...(decorate[o.value] || {}) }));
  }, [state.options, isInline, source]);

  if (isInline) {
    return { options: /** @type {*} */ (source), loading: false, error: null };
  }
  return { ...state, options: decorated };
};

export default useReferenceOptions;
